import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Section from './Section';

export default function ApprovalSection({ 
  proofs, 
  adminApproval, 
  clientApproval, 
  handleFileUpload, 
  handleApprovalChange,
  orderId,
  clientId,
  orderDetails,
  onSendForApproval,
  dealershipInfo
}) {
  const [showAdminHistory, setShowAdminHistory] = useState(false);
  const [showClientHistory, setShowClientHistory] = useState(false);

  const canSendForAdminApproval = proofs.length > 0;
  const canSendForClientApproval = adminApproval === 'Approved';

  // Sort proofs by uploadedAt timestamp (newest first)
  const sortProofsByDate = (proofArray) => {
    return [...proofArray].sort((a, b) => {
      // Handle cases where uploadedAt might be a Firestore timestamp or date
      const dateA = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt || 0);
      const dateB = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt || 0);
      return dateB - dateA; // Newest first
    });
  };

  // Filter and sort proofs by approval stage
  const adminProofs = sortProofsByDate(
    proofs.filter(proof => !proof.approvalStage || proof.approvalStage === 'admin')
  );
  
  const clientProofs = sortProofsByDate(
    proofs.filter(proof => proof.approvalStage === 'client')
  );

  // Get the most recent proof and older proofs for each stage
  const getLatestAndHistoryProofs = (proofArray) => {
    if (proofArray.length === 0) return { latest: null, history: [] };
    return {
      latest: proofArray[0], // Most recent
      history: proofArray.slice(1) // All others
    };
  };

  const adminProofData = getLatestAndHistoryProofs(adminProofs);
  const clientProofData = getLatestAndHistoryProofs(clientProofs);

  const formatProofDate = (proof) => {
    if (!proof.uploadedAt) return 'Unknown date';
    const date = proof.uploadedAt?.toDate ? proof.uploadedAt.toDate() : new Date(proof.uploadedAt);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const ProofItem = ({ proof, index, isLatest = false }) => (
    <div className={`proof-item ${isLatest ? 'latest-proof' : 'history-proof'}`}>
      <div className="proof-header">
        <span className="proof-date">{formatProofDate(proof)}</span>
        {isLatest && <span className="latest-badge">Latest</span>}
      </div>
      
      <iframe
        src={proof.fileUrl}
        title={`Proof ${index + 1}`}
        className="proof-iframe"
      />
      
      <div className="proof-actions">
        <a
          href={proof.fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="proof-link"
        >
          View Fullscreen
        </a>
        <span className={`proof-status ${
          proof.status === 'approved' ? 'approved' :
          proof.status === 'rejected' ? 'rejected' : 'pending'
        }`}>
          {proof.status}
        </span>
      </div>
      
      {proof.adminComment && (
        <p className="proof-comment">
          Admin: {proof.adminComment}
        </p>
      )}
      {proof.clientComment && (
        <p className="proof-comment">
          Client: {proof.clientComment}
        </p>
      )}
    </div>
  );

  return (
    <div className="design-approval-card">
      <Section title="Design & Approvals" defaultOpen={false}>
        <div className="design-approval-section">
          {/* Upload Proof */}
          <div className="upload-area">
            <label>Upload Art</label>
            <input
              type="file"
              onChange={(e) => handleFileUpload(e.target.files[0], 'proof')}
            />
            <p>Upload design files for review</p>
          </div>

          {/* Approval Workflow - Stacked (Admin above Client) */}
          <div className="approval-workflow-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Admin Approval (Left) */}
            <div className="approval-panel admin">
              <h3 style={{ textAlign: 'center', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                Admin Review
              </h3>
              
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div className={`approval-status-badge ${
                  adminApproval === 'Approved' ? 'approved' :
                  adminApproval === 'Rejected' ? 'rejected' : 'pending'
                }`}>
                  {adminApproval}
                </div>
              </div>

              <select
                value={adminApproval}
                onChange={(e) => handleApprovalChange(e.target.value, 'admin')}
                className="approval-select"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>

              <button
                onClick={() => onSendForApproval('admin')}
                disabled={!canSendForAdminApproval}
                className="approval-button admin"
              >
                Send for Admin Approval
              </button>
              
              {!canSendForAdminApproval && (
                <p className="error-message">
                  Upload artwork first
                </p>
              )}

              {/* Admin Proofs */}
              <div className="proof-section">
                <h4>Admin Review Proofs</h4>
                {adminProofData.latest ? (
                  <div>
                    {/* Latest Proof */}
                    <ProofItem proof={adminProofData.latest} index={0} isLatest={true} />
                    
                    {/* History Dropdown */}
                    {adminProofData.history.length > 0 && (
                      <div className="proof-history">
                        <button
                          onClick={() => setShowAdminHistory(!showAdminHistory)}
                          className="history-toggle-button"
                        >
                          <span>View History ({adminProofData.history.length} older versions)</span>
                          {showAdminHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        
                        {showAdminHistory && (
                          <div className="history-content">
                            {adminProofData.history.map((proof, index) => (
                              <ProofItem key={proof.id || index} proof={proof} index={index + 1} isLatest={false} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="no-proofs-message">
                    No admin proofs yet
                  </p>
                )}
              </div>
            </div>

            {/* Client Approval (Right) */}
            <div className="approval-panel client">
              <h3 style={{ textAlign: 'center', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                Client Review
              </h3>
              
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div className={`approval-status-badge ${
                  clientApproval === 'Approved' ? 'approved' :
                  clientApproval === 'Rejected' ? 'rejected' : 'pending'
                }`}>
                  {clientApproval}
                </div>
              </div>

              <select
                value={clientApproval}
                onChange={(e) => handleApprovalChange(e.target.value, 'client')}
                className="approval-select"
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>

              <button
                onClick={() => onSendForApproval('client')}
                disabled={!canSendForClientApproval}
                className="approval-button client"
              >
                Send for Client Approval
              </button>
              
              {!canSendForClientApproval && (
                <p className="error-message">
                  Admin approval required first
                </p>
              )}

              {/* Client Proofs */}
              <div className="proof-section">
                <h4>Client Review Proofs</h4>
                {clientProofData.latest ? (
                  <div>
                    {/* Latest Proof */}
                    <ProofItem proof={clientProofData.latest} index={0} isLatest={true} />
                    
                    {/* History Dropdown */}
                    {clientProofData.history.length > 0 && (
                      <div className="proof-history">
                        <button
                          onClick={() => setShowClientHistory(!showClientHistory)}
                          className="history-toggle-button"
                        >
                          <span>View History ({clientProofData.history.length} older versions)</span>
                          {showClientHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        
                        {showClientHistory && (
                          <div className="history-content">
                            {clientProofData.history.map((proof, index) => (
                              <ProofItem key={proof.id || index} proof={proof} index={index + 1} isLatest={false} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="no-proofs-message">
                    No client proofs yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}